import { Button, FormLabel, IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import { DeleteIconButton, DownIcon, PrintIconButton } from "../../../assets/common-svg/CommonSvg";
import FormikControl from "../../../bmr/components/common/FormikControl";
import { FieldArray, FormikProvider } from "formik";
import { formatDayNumber } from './common'
import { notify } from "../../../bmr/utils/methods/notification";
import { useState } from "react";

const FormikArrayPreScreen = ({
  onReprint = null,
  showPrintButton = false,
  specialCaseAdd = false,
  props,
  formik,
  disabledAll,
  handleQuestionClick,
  queriesFields,
  buttonPermission,
  showDeleteButton = true,
  bagDynamic,
  editingIndexes = {},
  onEditToggle = null,
  onSaveEdit = null,
  canEditBag = () => true,
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({ ...prev, [index]: isExpanded }));
  };

  const parseFieldValue = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return []; }
    }
    return [];
  };

  const printData = (index) => { onReprint(index) };

  const handleAddChildren = (props) => {
    const fieldName = props?.fieldAppend;
    const prevValues = formik?.values?.[fieldName] || [];
    const fieldKey = Object.keys(props.initialChildrenData)[0];
    const rawValue = formik?.values?.[fieldKey];
    const newValue = formatDayNumber(rawValue);
    if (!rawValue) { notify.error('Please enter a day valid day.'); return; }
    if (newValue < 1) { notify.error('Zero is not a valid day.'); return; }
    if (newValue < 5) { notify.error("The day must be greater than 5"); return; }
    const isDuplicate = prevValues.some(item => item[fieldKey] == newValue);
    if (isDuplicate) { notify.error("Day already exists"); return; }
    const isLess = prevValues.some(item => newValue < item[fieldKey]);
    if (isLess) { notify.error("The day must not be earlier than the previously used day."); return; }
    const newChild = { ...props.initialChildrenData, [fieldKey]: newValue };
    formik.setFieldValue(fieldName, [...prevValues, newChild]);
    formik.setFieldValue(fieldKey, "");
    const newIndex = prevValues.length;
    setExpandedAccordions(prev => ({ ...prev, [newIndex]: true }));
  };

  const getFirstChildValue = (index) => {
    const fieldArrayValues = parseFieldValue(formik.values[props.name]);
    if (!fieldArrayValues || !fieldArrayValues[index]) return 'New Entry';
    const childData = fieldArrayValues[index];
    const firstFieldKey = Object.keys(childData)[0];
    const firstFieldValue = childData[firstFieldKey];
    if (firstFieldValue !== undefined && firstFieldValue !== null && firstFieldValue !== '') {
      if (typeof firstFieldValue === 'number' || !isNaN(firstFieldValue)) return `Day ${firstFieldValue}`;
      return String(firstFieldValue);
    }
    return 'New Entry';
  };

  // ─── N/A helpers ─────────────────────────────────────────────────────────────
  // Resolves dotted field names like "cell_count.0.qc" against formik values
  const getNestedFormikValue = (values, name) =>
    name.split('.').reduce((acc, key) => acc?.[key], values);

  // Returns true when the field has naWhenEmpty, is disabled, and has no value
  const shouldShowNA = (subFieldProps) => {
    if (!subFieldProps.naWhenEmpty || !subFieldProps.disabled) return false;
    const val = getNestedFormikValue(formik.values, subFieldProps.name);
    return val === "" || val === null || val === undefined;
  };

  // Gray read-only box — same style as Day of Harvest N/A in ManufacturingForm
  const renderNABox = (subFieldProps, subFieldPropsIndex) => (
    <div key={subFieldPropsIndex} className="w-full relative">
      {subFieldProps.label && (
        <FormLabel className="text-[black] text-[0.75rem]">
          {subFieldProps.label}
        </FormLabel>
      )}
      <div
        className="mt-1 px-3 text-[0.875rem] text-[#828282] w-full"
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          minHeight: "40px",
          display: "flex",
          alignItems: "center",
          cursor: "not-allowed",
          boxSizing: "border-box",
        }}
      >
        N/A
      </div>
    </div>
  );
  // ─────────────────────────────────────────────────────────────────────────────

  const renderFieldWithQuestion = (subFieldProps, subFieldPropsIndex, questionInfo) => {
    // Show N/A gray box when naWhenEmpty + disabled + value is empty
    if (shouldShowNA(subFieldProps)) {
      return renderNABox(subFieldProps, subFieldPropsIndex);
    }

    return (
      <div key={subFieldPropsIndex} className="w-full relative">
        <FormikControl
          formik={formik}
          {...subFieldProps}
          disabled={questionInfo?.resolve == 0 ? false : subFieldProps.disabled}
        />
        {subFieldProps.note  && (
          <p className="text-[0.72rem] text-[#FB9D2C] mt-1 pl-1">
            <span className="font-semibold">Note: </span>{subFieldProps.note}
          </p>
        )}
        {!subFieldProps.noQuery && disabledAll && (
          <i
            onClick={(e) => handleQuestionClick(e, subFieldProps, questionInfo)}
            style={questionInfo?.resolve == 0 ? { color: "red" } : {}}
            className="fas fa-question-circle circle-icon cursor-pointer z-50 absolute top-[0.3rem] right-2"
            aria-hidden="true"
          />
        )}
        <Typography
          variant="span"
          className={`z-[1] font-semibold absolute right-5 ${
            formik.errors[subFieldProps.name] && formik.touched[subFieldProps.name]
              ? 'top-14' : 'top-10'
          } text-[0.7rem]`}
        >
          {subFieldProps.subLable === "power" ? (
            <p>x10<sup>6</sup>{subFieldProps?.extraLabel ? subFieldProps?.extraLabel : ' Cells'}</p>
          ) : (
            <span className="text-base">{subFieldProps.subLable}</span>
          )}
        </Typography>
      </div>
    );
  };

  const printButtonDynamic = (index, val) => (
    <div className="self-end md:self-center mt-3">
      <Tooltip title={`Print (${Number(val?.print_count)})`}>
        <span>
          <IconButton onClick={() => printData(index)}>
            <PrintIconButton bgColor={"#2F5597"} />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );

  const renderDeleteButton = (index, remove, disabledAll) => (
    <div className={
      (disabledAll || formik.values[props.name].length == 1)
        ? "self-end md:self-center mt-3 disable-add-visit-btn"
        : "self-end md:self-center mt-3"
    }>
      <Tooltip title="Delete">
        <span>
          <IconButton onClick={() => {
            (disabledAll || formik.values[props.name].length == 1) ? "" : remove(index)
          }}>
            <DeleteIconButton bgColor={"#FA5151"} />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );

  const renderAddButton = (push) => {
    if (specialCaseAdd || ((props.multipleField || props?.showAddBtn) && !disabledAll && buttonPermission)) {
      return (
        <div className={props?.flexItem ? 'mb-4 mr-2' : "text-end m-2"}>
          <Button
            type="button"
            disabled={specialCaseAdd ? !specialCaseAdd : disabledAll}
            onClick={() => props?.showAddBtn ? handleAddChildren(props) : push(props.initialChildrenData || {})}
            className="mt-4"
            fullWidth
          >
            Add
          </Button>
        </div>
      );
    }
    return null;
  };

  const getContainerClass = (props, isAccordion = false) => {
    if (isAccordion) {
      return props?.shouldFlexDel
        ? 'mt-3 flex gap-x-4 w-full'
        : props.col && `flex-1 grid grid-cols-1 md:grid-cols-${props.col} gap-4 w-full`;
    }
    if (props?.flexItem) return `flex w-full m-2`;
    if (props.col) return `p-4 flex flex-col md:flex-row gap-4 items-start md:items-center`;
    return `flex-1 grid grid-cols-1 md:grid-cols-1 gap-4 w-full`;
  };

  const getMainContainerClass = (props) => {
    if (props?.flexItem) return `flex border rounded-lg shadow-sm bg-white`;
    if (!props.col) return `flex flex-col md:flex-row gap-4 items-start md:items-center`;
    if (props?.shouldFlexDel) return '';
    return `border rounded-lg shadow-sm bg-white m-2`;
  };

  const hasRowErrors = (index) => {
    const errors = formik.errors[props.name];
    if (!errors) return false;
    if (Array.isArray(errors)) {
      const rowError = errors[index];
      return rowError && (
        typeof rowError === 'string' ||
        (typeof rowError === 'object' && Object.keys(rowError).length > 0)
      );
    }
    if (typeof errors === 'object' && errors[index]) return true;
    return false;
  };

  const renderAccordionView = (push, remove) => (
    <div className={getMainContainerClass(props)}>
      {parseFieldValue(formik.values[props.name])?.map((_, index) => (
        <Accordion
          key={index}
          expanded={expandedAccordions[index] || false}
          onChange={handleAccordionChange(index)}
          className="w-full !rounded-lg !shadow-sm !mb-2"
          sx={{ border: `1px solid #C3C3C3` }}
        >
          <AccordionSummary
            expandIcon={<div className="d-flex gap-3 align-items-center"><DownIcon /></div>}
            aria-controls={`panel-${props.name}-${index}-content`}
            id={`panel-${props.name}-${index}-header`}
          >
            <div className="font-bold text-[14px] flex items-center gap-2 w-full">
              {getFirstChildValue(index)}
              {index == 0 && hasRowErrors(index) && (
                <span className="text-red-500 text-xs font-normal">(Please fill the required fields)</span>
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className={getContainerClass(props, true)}>
              {props.children(index)
                .filter((_, subFieldPropsIndex) => subFieldPropsIndex !== 0)
                .map((subFieldProps, subFieldPropsIndex) => {
                  const questionInfo = queriesFields?.find(op => op.fields === subFieldProps.name);
                  const isLastField = subFieldPropsIndex === props.children(index).length - 2;
                  return (
                    <>
                      {renderFieldWithQuestion(subFieldProps, subFieldPropsIndex, questionInfo)}
                      {(subFieldProps.multipleField || props?.shouldFlexDel) && isLastField && showDeleteButton && (
                        renderDeleteButton(index, remove, disabledAll)
                      )}
                    </>
                  );
                })}
            </div>
            {props.multipleField && showDeleteButton && renderDeleteButton(index, remove, disabledAll)}
          </AccordionDetails>
        </Accordion>
      ))}
      {renderAddButton(push)}
    </div>
  );

  const renderNormalView = (push, remove) => {
    let disabledBtn = true;
    return (
      <>
        {parseFieldValue(formik.values[props.name])?.map((rowVal, index) => {
          const hasMatchingIdentifier =
            bagDynamic && bagDynamic?.[index] && Object.keys(bagDynamic?.[index]).length > 0;
          const canDelete =
            props.multipleField &&
            (props?.shouldItemDel ? !hasMatchingIdentifier : !disabledBtn);
          const isExistingRow = hasMatchingIdentifier;
          const isEditingThisRow = !!editingIndexes?.[index];

          return (
            <div key={'_next' + index} className={getContainerClass(props, false)}>
              <div className={
                (props?.flexItem || props?.object)
                  ? 'flex justify-between items-center w-full gap-2'
                  : props.col && `flex-1 grid grid-cols-1 md:grid-cols-${props.col} gap-4 w-full`
              }>
                {props?.children(index)?.map((subFieldProps, subFieldPropsIndex) => {
                  if (subFieldProps?.type === 'label_item') {
                    return (
                      <span className="whitespace-nowrap font-bold" key={subFieldPropsIndex}>
                        {subFieldProps?.label}
                      </span>
                    );
                  } else {
                    disabledBtn = subFieldProps?.disabled;
                    const questionInfo = queriesFields?.find(op => op.fields === subFieldProps.name);
                    return renderFieldWithQuestion(subFieldProps, subFieldPropsIndex, questionInfo);
                  }
                })}
              </div>

              {canDelete && renderDeleteButton(index, remove, props?.shouldItemDel ? false : disabledBtn)}
              {showPrintButton && (props?.shouldItemDel ? hasMatchingIdentifier : disabledBtn) && printButtonDynamic(index, rowVal)}

              {isExistingRow && onEditToggle && onSaveEdit && canEditBag(index) && (
                <div className="self-end md:self-center mt-3">
                  {isEditingThisRow ? (
                    <Tooltip title="Save Changes">
                      <span>
                        <Button type="button" size="small" variant="contained" color="success"
                          onClick={() => onSaveEdit(index)} className="!min-w-[60px] !h-[32px] !text-xs">
                          Save
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Edit">
                      <span>
                        <Button type="button" size="small" variant="outlined"
                          onClick={() => onEditToggle(index)} className="!min-w-[60px] !h-[32px] !text-xs">
                          Edit
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {renderAddButton(push)}
      </>
    );
  };

  return (
    <FormikProvider value={formik}>
      <FieldArray name={props.name}>
        {({ push }) => {
          const handleRemove = (index) => {
            const arrayCopy = [...formik.values[props.name]];
            arrayCopy.splice(index, 1);
            const updatedArray = arrayCopy.map((item, idx) => ({
              ...item,
              ...(Object.prototype.hasOwnProperty.call(item, "identifier") && { identifier: idx + 1 })
            }));
            formik.setFieldValue(props.name, updatedArray);
          };
          return (
            <div className={getMainContainerClass(props)}>
              {props?.useAccordion
                ? renderAccordionView(push, handleRemove)
                : renderNormalView(push, handleRemove)
              }
            </div>
          );
        }}
      </FieldArray>
    </FormikProvider>
  );
}

export default FormikArrayPreScreen;